import {Router} from "@angular/router";
import {Account} from "../../models/account";
import {ReplaySubject} from "rxjs";
import {Utils} from "../../utils";
import {environment} from "@environments/environment";
import {Company} from "../../models/company";
import {RestApiManager} from "../rest-api-manager";
import {Role} from "../../models/role";
import {Injectable} from "@angular/core";
import {IAccountManagerService} from "./iaccount-manager-service";
import {DEBUG, DebugService} from "../debug.service";
import {NullValidationHandler, OAuthService, OAuthStorage, UserInfo} from "angular-oauth2-oidc";
import {differenceInSeconds} from "date-fns";
import {AngularFireAuth} from "@angular/fire/auth";
import {BaseAccountManagerService} from "./base-account-manager-service";
import {AuthConfig} from "angular-oauth2-oidc/auth.config";

@Injectable({
  providedIn: 'root'
})
export class KeycloakAccountManagerService extends BaseAccountManagerService implements IAccountManagerService {

  static keycloakEndpoint = `${environment.keycloak.baseEndpoint}/${environment.keycloak.realm}`;

  account: Account
  onLoginStatusChanged: ReplaySubject<boolean>;
  getRoles: (id: string) => Promise<Role[]>;
  isAdminOrExpert: () => boolean;

  refreshing: boolean;
  initialized: boolean;

  constructor(
    private router: Router,
    private restApiManager: RestApiManager,
    private debugService: DebugService,
    private oAuthService: OAuthService,
    private oAuthStorage: OAuthStorage,
    private auth: AngularFireAuth,
  ) {
    super(router);
    this.configureOAuthService();
  }

  configureOAuthService() {
    const oauthConfig: AuthConfig = {
      oidc: true,
      issuer: KeycloakAccountManagerService.keycloakEndpoint,
      clientId: environment.keycloak.clientId,
      redirectUri: environment.keycloak.redirectUri == 'implicit' ? document.location.origin : environment.keycloak.redirectUri,
      responseType: environment.keycloak.responseType,
      scope: environment.keycloak.scope,
      requireHttps: environment.keycloak.requireHttps,
      showDebugInformation: environment.keycloak.showDebugInformation,
      disableAtHashCheck: environment.keycloak.disableAtHashCheck
    };
    this.oAuthService.configure(oauthConfig);
    this.oAuthService.setStorage(this.oAuthStorage);
    this.oAuthService.tokenValidationHandler = new NullValidationHandler();

    // Load OIDC configuration
    this.oAuthService.loadDiscoveryDocument().then(async status => {
      this.debugService.log(DEBUG.LOGIN, 'OAuth discovery event:', status);

      // Check if the user is already logged in
      if (!this.oAuthService.getIdToken()) await this.oAuthService.tryLogin();

      this.oAuthService.setupAutomaticSilentRefresh();
      this.initialized = true;

    });
  }

  waitOAuthInitialization() {
    return new Promise((resolve, reject) => {
      setInterval(() => {
        if (this.initialized) resolve();
      }, 1000);
    });
  }

  configureAccessTokenFetcher() {
    this.account.authTokenFetcher = () => this.oAuthService.getAccessToken();
  }

  async login(email: string, password: string): Promise<boolean> {

    this.oAuthService.oidc = false;

    let user: UserInfo;

    try {

      // Login and fetch token and refresh token
      user = await this.oAuthService.fetchTokenUsingPasswordFlowAndLoadUserProfile(email, password);

    } catch (e) {
      return false;
    }

    if (await this.postLogin(user, this.oAuthService.getAccessToken())) {
      await this.router.navigate(['/']);
      return true;
    } else {
      return false;
    }

  }

  async ssoLogin(accessToken: string, refreshToken: string): Promise<boolean> {

    this.oAuthService.oidc = false;

    // Clear sso storage
    await this.oAuthService.revokeTokenAndLogout();

    // Set access token and refresh token
    this.oAuthStorage.setItem('access_token', accessToken);
    this.oAuthStorage.setItem('refresh_token', refreshToken);

    // If login is valid refresh session with new tokens
    try {

      const loggedIn = await this.isLoggedIn();

      if (loggedIn) await this.oAuthService.refreshToken();

      return loggedIn;

    } catch (e) {
      return false;
    }

  }

  initSSOLoginFlow(): void {
    this.oAuthService.oidc = true;
    return this.oAuthService.initLoginFlow();
  }

  async loginAnonymous(firstName?: string, lastName?: string): Promise<boolean> {
    // Anonymous login is not implemented with keycloak
    throw new Error('Not Implemented');
  }

  async loginWithCustomToken(token: string): Promise<boolean> {
    // Custom token login is not implemented with keycloak
    throw new Error('Not Implemented');
  }

  async postLogin(user: UserInfo, authToken: string, userOnly: boolean = false): Promise<boolean> {

    // Skip if already loaded
    if (this.account.validate()) {
      this.startTokenAutoRefresh(user);
      return true;
    }

    if (Utils.isEmpty(user.sub) || Utils.isEmpty(authToken)) {
      return false;
    }

    this.account.authProviderId = user.sub;

    // Get user account
    const account = await this.restApiManager.get<Account>(
      Account,
      `${environment.restApi.endpoint}/users/uid/${this.account.authProviderId}`,
      this.account.authToken
    );

    // Set account properties
    this.account.id = account.id;
    this.account.firstName = account.firstName;
    this.account.lastName = account.lastName;
    this.account.isAnonymous = await this.isAnonymous();

    if (!userOnly) {

      // Get company
      this.account.company = await this.restApiManager.get<Company>(
        Company,
        `${environment.restApi.endpoint}/companies/${account.company.id}`,
        this.account.authToken
      );

      // Get roles
      this.account.roles = await this.getRoles(account.id);

      // Logout if not expert
      if (!this.isAdminOrExpert()) {
        return false;
      }

    }

    const accountResult = this.account.validate();

    if (accountResult) {
      this.startTokenAutoRefresh(user);
    }

    this.onLoginStatusChanged.next(accountResult);

    // Login anonymously with Firebase
    this.debugService.log(DEBUG.AUTH, 'Logging in anonymously with Firebase...');
    await this.auth.signInAnonymously();

    return accountResult;

  }

  async isLoggedIn(): Promise<boolean> {

    await this.waitOAuthInitialization();

    // Redirect to login if access token is not valid and cannot refresh anymore
    if (!this.oAuthService.hasValidAccessToken()) {
      try {
        await this.oAuthService.refreshToken();
      } catch (ex) {
        this.goToLoginPage(true);
        return false;
      }
    }

    try {
      return this.oAuthService.hasValidAccessToken() &&
        !(await this.isAnonymous()) &&
        await this.postLogin(await this.oAuthService.loadUserProfile(), this.oAuthService.getAccessToken());
    } catch (ex) {
      this.goToLoginPage(true);
      return false;
    }
  }

  isAnonymous(): Promise<boolean> {
    // Anonymous login is not implemented with keycloak
    return Promise.resolve(false);
  }

  async requestPIN(): Promise<string> {
    return (await this.restApiManager.post<{ pin: string }>(
      `${environment.restApi.endpoint}/auth/pin`,
      { },
      this.account.authToken
    )).pin;
  }

  async logout() {

    // Go to login page
    this.goToLoginPage();

    // Clear profile
    this.account.clear();

    // Update login status
    this.onLoginStatusChanged.next(false);

    // Logout from Keycloak
    await this.oAuthService.revokeTokenAndLogout();

    // Logout from Firebase
    await this.auth.signOut();

  }

  async startTokenAutoRefresh(user: UserInfo): Promise<void> {

    this.debugService.log(DEBUG.AUTH, 'Refresh token task requested');

    // Check if already refreshing
    if (this.refreshing) {
      return;
    }

    this.debugService.log(DEBUG.AUTH, 'Starting refreshing token...');

    this.refreshing = true;

    const tokenRefreshFn = async () => {

      // Get token expiration time
      const secondsToExpiration = differenceInSeconds(this.oAuthService.getAccessTokenExpiration(), Date.now());
      this.debugService.log(DEBUG.AUTH, `Token will expire in ${secondsToExpiration} seconds`);
      if (secondsToExpiration > 35) await Utils.sleep((secondsToExpiration - 30) * 1000);

      // Try to refresh token with refresh token
      try {

        this.debugService.log(DEBUG.AUTH, 'Refreshing auth token...');
        await this.oAuthService.refreshToken();
        this.debugService.log(DEBUG.AUTH, 'Refreshing auth token success');
        tokenRefreshFn();

      } catch (ex) {

        // Stop refreshing if token cannot be refreshed
        this.debugService.error(DEBUG.AUTH, 'Refresh token failed');
        this.debugService.log(DEBUG.AUTH, 'Stopping refreshing token...');
        this.refreshing = false;

      }

    }

    tokenRefreshFn();

  }

}
